import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { jobsApi, proposalsApi, dashboardApi } from '../services/api';
import type { Job, Proposal } from '../types';
import { JobCard } from '../components/jobs/JobCard';
import { ProposalTile } from '../components/obros/ProposalTile';
import { MaterialIcon } from '../components/ui/MaterialIcon';
import { SkeletonCard } from '../components/ui/Skeleton';
import { useAuthStore } from '../stores/authStore';

interface StatDef {
  label: string;
  value?: number;
}

// Calca ScreenMisTrabajos de screens-main.jsx (Claude Design): tabs + banda de stats + badge
// de "X propuestas" flotando sobre el JobCard en vez de una pestaña aparte.
const StatsBand: React.FC<{ stats: StatDef[] }> = ({ stats }) => (
  <div className="mx-4 mt-3.5 mb-1 px-4 py-3 rounded-2xl bg-gradient-to-br from-cream to-cream2 flex gap-4">
    {stats.map((s) => (
      <div key={s.label} className="flex-1">
        <p className="text-xl font-extrabold text-onSurface tracking-tight leading-none">{s.value ?? '—'}</p>
        <p className="text-[11px] text-onSurface-var font-medium mt-1">{s.label}</p>
      </div>
    ))}
  </div>
);

const Tabs: React.FC<{ tabs: string[]; active: number; onChange: (i: number) => void }> = ({ tabs, active, onChange }) => (
  <div className="flex bg-surface border-b border-outlineVarWarm">
    {tabs.map((t, i) => (
      <button
        key={t}
        onClick={() => onChange(i)}
        className={`flex-1 h-12 text-sm relative ${active === i ? 'font-bold text-primary-700' : 'font-medium text-onSurface-var'}`}
      >
        {t}
        {active === i && <span className="absolute bottom-0 left-[15%] right-[15%] h-[3px] bg-primary-500 rounded-t-full" />}
      </button>
    ))}
  </div>
);

const EmptyHint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mx-4 rounded-2xl border border-dashed border-outlineWarm bg-surface/60 px-5 py-8 text-center text-sm text-onSurface-mute">
    {children}
  </div>
);

export const MyJobs: React.FC = () => {
  const { user } = useAuthStore();
  return user?.user_type === 'worker' ? <WorkerMyJobs /> : <ClientMyJobs />;
};

const clientTabs = [
  { label: 'Publicados', status: undefined },
  { label: 'En progreso', status: 'in_progress' },
  { label: 'Completados', status: 'completed' },
];

const ClientMyJobs: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [stats, setStats] = useState<{ open_jobs: number; in_progress_jobs: number; total_proposals: number; unread_messages: number } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardApi.client().then(({ data }) => setStats(data));
  }, []);

  useEffect(() => {
    setLoading(true);
    jobsApi
      .myJobs({ status: clientTabs[tab].status })
      .then(({ data }) => setJobs(data.data ?? data))
      .finally(() => setLoading(false));
  }, [tab]);

  return (
    <div className="bg-surface-dim min-h-full">
      <div className="max-w-xl mx-auto">
      <div className="px-4 py-4">
        <h1 className="text-xl font-bold text-onSurface">Mis trabajos</h1>
      </div>
      <Tabs tabs={clientTabs.map((t) => t.label)} active={tab} onChange={setTab} />
      <StatsBand
        stats={[
          { label: 'Activos', value: stats ? stats.open_jobs + stats.in_progress_jobs : undefined },
          { label: 'Propuestas', value: stats?.total_proposals },
          { label: 'Mensajes', value: stats?.unread_messages },
        ]}
      />

      <div className="flex flex-col gap-3 px-4 py-4">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)
        ) : jobs.length === 0 ? (
          <EmptyHint>No hay trabajos en esta pestaña todavía.</EmptyHint>
        ) : (
          jobs.map((job) => (
            <div key={job.id} className="relative">
              <JobCard job={job} />
              {job.status === 'open' && !!job.proposals_count && (
                <Link
                  to={`/jobs/${job.id}/proposals`}
                  className="absolute top-3.5 right-3.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-onSurface text-cream text-[11px] font-bold"
                >
                  <MaterialIcon name="mail" size={12} />
                  {job.proposals_count} {job.proposals_count === 1 ? 'propuesta' : 'propuestas'}
                </Link>
              )}
            </div>
          ))
        )}
      </div>
      </div>
    </div>
  );
};

const workerTabs = ['Mis propuestas', 'En progreso', 'Historial'];

const WorkerMyJobs: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [stats, setStats] = useState<{ pending_proposals: number; accepted_proposals: number; completed_jobs: number } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardApi.worker().then(({ data }) => setStats(data));
  }, []);

  useEffect(() => {
    setLoading(true);
    proposalsApi
      .myProposals({})
      .then(({ data }) => setProposals(data.data ?? data))
      .finally(() => setLoading(false));
  }, [tab]);

  const visible = proposals.filter((p) => {
    if (tab === 0) return true;
    if (tab === 1) return p.status === 'accepted' && p.job?.status === 'in_progress';
    return p.job?.status === 'completed' || p.status === 'rejected' || p.status === 'withdrawn';
  });

  return (
    <div className="bg-surface-dim min-h-full">
      <div className="max-w-xl mx-auto">
      <div className="px-4 py-4">
        <h1 className="text-xl font-bold text-onSurface">Mis trabajos</h1>
      </div>
      <Tabs tabs={workerTabs} active={tab} onChange={setTab} />
      <StatsBand
        stats={[
          { label: 'Pendientes', value: stats?.pending_proposals },
          { label: 'En curso', value: stats?.accepted_proposals },
          { label: 'Ganados', value: stats?.completed_jobs },
        ]}
      />

      <div className="flex flex-col gap-3 px-4 py-4">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)
        ) : visible.length === 0 ? (
          <EmptyHint>No hay nada para mostrar en esta pestaña.</EmptyHint>
        ) : (
          visible.map((p) => <ProposalTile key={p.id} proposal={p} perspective="worker" />)
        )}
      </div>
      </div>
    </div>
  );
};
